import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import type { Post } from '../../types/api';

export const BlogShowPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<Post | null>(null);
    const [relatedPosts, setRelatedPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        setError(null);
        api.getPost(slug).then(res => {
            if (res.data?.post) {
                setPost(res.data.post);
                setRelatedPosts(res.data.relatedPosts || []);
            } else {
                setError('Article not found.');
            }
        }).catch(err => {
            setError(err.message || 'Article not found.');
        }).finally(() => {
            setLoading(false);
        });
    }, [slug]);

    if (loading) {
        return (
            <div className="py-24 text-center text-slate-400">
                <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p>Loading article...</p>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="py-24 text-center text-slate-400 space-y-4">
                <h1 className="text-2xl font-bold text-white">Article Not Found</h1>
                <p>{error || "The requested article does not exist or has been unpublished."}</p>
                <Link to="/blog" className="inline-block px-5 py-2 rounded-full bg-slate-900 border border-slate-700 text-sm font-semibold text-emerald-400">
                    &larr; Back to Perspectives
                </Link>
            </div>
        );
    }

    const currentUrl = window.location.href;

    return (
        <div>
            {/* Article Header */}
            <header className="py-16 bg-radial-glow border-b border-slate-800/80">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
                    
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                        <Link to="/blog" className="text-slate-400 hover:text-white transition">
                            &larr; All Articles
                        </Link>
                        <span className="text-slate-600">•</span>
                        {post.category && (
                            <Link to={`/blog?category=${post.category.slug}`} className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold uppercase tracking-wider">
                                {post.category.name}
                            </Link>
                        )}
                        <span className="text-slate-600">•</span>
                        <span className="text-slate-400">{post.reading_time_minutes} min read</span>
                        <span className="text-slate-600">•</span>
                        <time className="text-slate-400" dateTime={post.published_at || undefined}>
                            {post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                        </time>
                    </div>

                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
                        {post.title}
                    </h1>

                    {post.excerpt && (
                        <p className="text-lg sm:text-xl text-slate-300 leading-relaxed font-light">
                            {post.excerpt}
                        </p>
                    )}

                    {/* Author Byline */}
                    <div className="pt-4 flex items-center justify-between border-t border-slate-800/80">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-800 ring-1 ring-emerald-500/50">
                                <img src="/rakib image.jpeg" alt="Rakib Hasan" className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <span className="text-sm font-bold text-white block">Rakib Hasan</span>
                                <span className="text-xs text-slate-400 block">President • English Knowledge Holders</span>
                            </div>
                        </div>

                        {/* Share Buttons */}
                        <div className="flex items-center gap-2">
                            <a 
                                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(currentUrl)}`} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="px-3 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-xs font-semibold text-slate-300 hover:text-white hover:border-slate-600 transition"
                            >
                                Share on X
                            </a>
                            <a 
                                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="px-3 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-xs font-semibold text-slate-300 hover:text-white hover:border-slate-600 transition"
                            >
                                LinkedIn
                            </a>
                        </div>
                    </div>

                </div>
            </header>

            {/* Article Body Content */}
            <article className="py-16 bg-slate-950">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    {post.cover_image && (
                        <div className="mb-12 rounded-3xl overflow-hidden shadow-2xl border border-slate-800 bg-slate-900">
                            <img 
                                src={post.cover_image.startsWith('/') ? post.cover_image : `/storage/${post.cover_image}`} 
                                alt={post.title} 
                                className="w-full h-auto max-h-[520px] object-cover" 
                                loading="eager"
                            />
                        </div>
                    )}

                    <div 
                        className="text-slate-300 leading-relaxed space-y-6 text-base lg:text-lg"
                        dangerouslySetInnerHTML={{ __html: post.body }}
                    />

                    {/* Tags List */}
                    {post.tags && post.tags.length > 0 && (
                        <div className="mt-12 pt-8 border-t border-slate-800/80 flex flex-wrap items-center gap-2">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mr-2">Tags:</span>
                            {post.tags.map(tag => (
                                <Link key={tag.id} to={`/blog?tag=${tag.slug}`} className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-900 text-slate-300 hover:text-emerald-400 border border-slate-800 transition">
                                    #{tag.name}
                                </Link>
                            ))}
                        </div>
                    )}

                    {/* Author Card Box */}
                    <div className="mt-16 p-8 rounded-3xl glass-card flex flex-col sm:flex-row items-center sm:items-start gap-6">
                        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-900 shrink-0 ring-2 ring-emerald-500/40">
                            <img src="/rakib image.jpeg" alt="Rakib Hasan" className="w-full h-full object-cover" />
                        </div>
                        <div className="space-y-2 text-center sm:text-left">
                            <h2 className="text-lg font-bold text-white">Rakib Hasan</h2>
                            <p className="text-xs text-emerald-400 font-semibold uppercase tracking-wider">Rakib's Care</p>
                            <p className="text-sm text-slate-400 leading-relaxed">
                                Student leader, organizer, and youth advocate from Habiganj, Bangladesh. President of English Knowledge Holders and Treasurer of Interact Club of Habiganj.
                            </p>
                            <div className="pt-2">
                                <Link to="/about" className="text-xs font-bold text-emerald-400 hover:text-emerald-300 transition">
                                    Read full biography &rarr;
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Related Posts */}
                    {relatedPosts.length > 0 && (
                        <div className="mt-16 pt-12 border-t border-slate-800/80">
                            <h3 className="text-2xl font-bold text-white mb-6">Related Articles</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {relatedPosts.map(related => (
                                    <div key={related.id} className="p-5 rounded-2xl glass-card glass-card-hover space-y-3">
                                        <span className="text-xs text-emerald-400 font-semibold">{related.reading_time_minutes} min read</span>
                                        <h4 className="text-base font-bold text-white leading-snug">
                                            <Link to={`/blog/${related.slug}`} className="hover:text-emerald-400 transition">
                                                {related.title}
                                            </Link>
                                        </h4>
                                        <p className="text-xs text-slate-400 line-clamp-2">{related.excerpt}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </article>
        </div>
    );
};
